import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Translate from "../../utils/Translate";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { useCallback, useEffect, useState } from "react";
import { partType } from "../../utils/part";
import { useApi } from "../../utils/Api";
import { ePortCode } from "../../utils/ePortCodes";
import { useLocation, useNavigate } from "react-router-dom";
import Preloader from "../Preloader";
import { DataGridPro as DataGrid, useGridApiRef } from "@mui/x-data-grid-pro";
import { dataGridLang } from "../MemToolBar";
import { ToolBar } from "../ToolBar";
import { IconAndLabelColumnHeader } from "../../utils/dataGrid";
import { Add, Delete, Edit, ExpandMore, Numbers, ScatterPlot, ShoppingBasket } from "@mui/icons-material";
import DotMenu from "../DotMenu";
import { CustomNoRowsOverlay } from "../../utils/StyledGridOverlay";
import MultiPartChildDialog from "./MultiPartChildDialog";
import MultiPartChildForm from "./MultiPartChildForm";
import { AlertDialog } from "../AlertDialog";
import { DatePicker } from "@mui/x-date-pickers";
import { getCurrentDateMask, getCurrentInputFormat } from "../../utils/dateTimeFormat";

const _disabledPartTypes = [
  partType.course,
  partType.plasticCardPrintJob,
  partType.plasticCardPrintJobAndDigitalLicense,
  partType.ePortProgamLicense,
  partType.ePortUserLicense,
  partType.courseAdminHTML,
  partType.courseAdminReadOnly,
  partType.drivingPermitsHTML,
  partType.drivingPermitsHTMLUser,
  partType.SMS,
  partType.ePortReportClient,
  partType.iframe,
  partType.userReadOnly,
  partType.digitalLicense,
  partType.license,
  partType.freight,
  partType.freight06Vat,
  partType.freight12Vat,
  partType.freight25Vat,
  partType.freightEUVat,
  partType.freightOutsideEUVat,
];

export default function PartDialogForm({ partId, onClose, onSaved }) {
  const [part, setPart] = useState({});
  const [departmentCodes, setDepartmentCodes] = useState([]);
  const [accountNoCodes, setAccountNoCodes] = useState([]);
  const [partPrices, setPartPrices] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState(null);
  const [areas, setAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showMultiPartChildDialog, setShowMultiPartChildDialog] = useState(false);
  const [editingMultiPartChild, setEditingMultiPartChild] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const api = useApi();
  const apiRef = useGridApiRef();
  const location = useLocation();
  const navigate = useNavigate();
  const localizedTextsMap = dataGridLang();

  useEffect(() => {
    async function init() {
      setIsLoading(true);
      const partResponse = await loadPart(partId, api);
      if (partResponse) {
        setPart(partResponse);
      }

      const [departmentsResponse, accountsResponse, areas, prices, currenciesResponse] = await Promise.all([
        loadEPortCodes(ePortCode.department, api),
        loadEPortCodes(ePortCode.account, api),
        loadAreas(api),
        loadPartPrices(partId, api),
        loadCurrencies(api),
      ]);

      setDepartmentCodes(departmentsResponse?.ePortCodes ?? []);
      setAccountNoCodes(accountsResponse?.ePortCodes ?? []);
      setAreas(areas ?? []);
      setPartPrices(prices ?? []);
      setCurrencies(currenciesResponse?.currencies ?? []);

      if (departmentsResponse && !partId && !part.department) {
        setPart((prev) => ({ ...prev, department: departmentsResponse.defaultEPortCodeId }));
      }

      if (currenciesResponse) {
        setSelectedCurrencyCode(currenciesResponse.defaultCurrencyCode);
      }
      setIsLoading(false);
    }
    init();
  }, [api]);

  const closeAndReload = useCallback(() => {
    //Soft reload with redirect
    const loc = encodeURIComponent(location.pathname);
    navigate(`/redirect/${loc}`, { replace: true });
  }, [navigate, location]);

  const handleUpdatePartValue = useCallback((name, value) => setPart((prev) => ({ ...prev, [name]: value })), []);

  const handleUpdatePartPriceValue = useCallback(
    (currency, newPrice) =>
      setPartPrices((prev) => {
        newPrice = !!newPrice ? newPrice : 0;
        if (!currency) return prev;

        const index = prev.findIndex((pp) => pp.currency === currency);
        if (index !== -1) {
          const partPrice = prev[index];
          partPrice.price = newPrice;
          return prev.toSpliced(index, 1, partPrice);
        } else {
          return [...prev, { currency, price: newPrice }];
        }
      }),
    [],
  );

  const handleSaveDialog = useCallback(
    async (part, partPrices) => {
      const partResponse = await savePart(part, api);
      if (!partResponse.isSuccessful) {
        return;
      }

      const pricesResponse = await savePartPrices(partResponse.partId, partPrices, api);
      if (!pricesResponse.isSuccessful) {
        return;
      }

      if (onSaved != null) {
        onSaved();
      }

      if (onClose != null) {
        onClose();
      } else {
        closeAndReload();
      }
    },
    [api, closeAndReload, onSaved],
  );

  const handleMultiPartChildAdded = useCallback(
    (newPart, childPartQty) => {
      const existingChild = (part.children ?? []).find((ch) => ch.child.partId === newPart.partId);
      if (existingChild) {
        setErrorMessage(Translate.get());
        return;
      }

      setPart((prev) => ({
        ...prev,
        children: [...(prev.children ?? []), { child: newPart, childQty: childPartQty }],
      }));
      setShowMultiPartChildDialog(false);
    },
    [part],
  );

  const handleMultiPartChildEdited = useCallback(
    (editedPart, childPartQty) => {
      const existingChildIndex = part.children.findIndex((ch) => ch.child.partId === editedPart.partId);
      if (existingChildIndex === -1) {
        throw new Error("handleMultiPartChildEdited part has not yet been added as a child");
      }

      setPart((prev) => ({
        ...prev,
        children: prev.children.toSpliced(existingChildIndex, 1, { child: editedPart, childQty: childPartQty }),
      }));
      setShowMultiPartChildDialog(false);
      setEditingMultiPartChild(null);
    },
    [part],
  );

  const handleMultiPartChildDeleted = useCallback(
    (deletedPart) => {
      const existingChildIndex = part.children.findIndex((ch) => ch.child.partId === deletedPart.partId);
      if (existingChildIndex === -1) {
        throw new Error("handleMultiPartChildEdited part has not yet been added as a child");
      }

      setPart((prev) => ({
        ...prev,
        children: prev.children.toSpliced(existingChildIndex, 1),
      }));
    },
    [part],
  );

  function getMultiPartDotMenuActions({ childQty, child }) {
    return [
      // {
      //   icon: orderLine.excludeFromStats ? <Visibility /> : <VisibilityOff />,
      //   text: orderLine.excludeFromStats
      //     ? Translate.get("IncludeOrderLineInStats")
      //     : Translate.get("ExcludeOrderLineFromStats"),
      //   disabled: !canUpdate || !order.creditId || true, // TODO Stöd ej i BE än eftersom fältet bara finns i interna och är redan NOT NULL
      //   onClick: () =>
      //     handleSimpleOrderLineFieldChanged(
      //       orderLine.orderLineId,
      //       "excludeFromStats",
      //       !orderLine.excludeFromStats
      //     ),
      // },
      {
        icon: <Edit />,
        text: Translate.get("Alter"),
        onClick: () => {
          setShowMultiPartChildDialog(true);
          setEditingMultiPartChild({ childQty, child });
        },
      },
      {
        icon: <Delete />,
        text: Translate.get("Delete"),
        onClick: () => handleMultiPartChildDeleted(child),
      },
    ];
  }

  const multiPartColumns = [
    {
      field: "partNo",
      flex: 0.3,
      headerName: Translate.get("PartNoLabel"),
      renderHeader: () => <IconAndLabelColumnHeader icon={Numbers} label={Translate.get("PartNoLabel")} />,
      valueGetter: (value, row, column, apiRef) => row.child?.partNo ?? "",
    },
    {
      field: "partId",
      flex: 1,
      headerName: Translate.get("PartLabel"),
      renderHeader: () => <IconAndLabelColumnHeader icon={ShoppingBasket} label={Translate.get("PartLabel")} />,
      valueGetter: (value, row, column, apiRef) => row.child?.description ?? "",
    },
    {
      field: "qty",
      flex: 0.3,
      headerName: Translate.get("Quantity"),
      renderHeader: () => <IconAndLabelColumnHeader icon={ScatterPlot} label={Translate.get("Quantity")} />,
      valueGetter: (value, row, column, apiRef) => row.childQty ?? "",
    },
    {
      field: "dotMenu",
      flex: 0.2,
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      headerClassName: "hideSeparator",
      align: "right",
      hideable: false,
      renderCell: (params) =>
        getMultiPartDotMenuActions && <DotMenu content={getMultiPartDotMenuActions(params.row)} horizontal />,
    },
  ];

  return (
    <>
      {isLoading && <Preloader />}
      {showMultiPartChildDialog && (
        <MultiPartChildDialog
          onCancel={() => {
            setShowMultiPartChildDialog(false);
            setEditingMultiPartChild(null);
          }}
          onOk={editingMultiPartChild ? handleMultiPartChildEdited : handleMultiPartChildAdded}
          currentPart={editingMultiPartChild ? editingMultiPartChild.child : null}
          currentChildPartQty={editingMultiPartChild ? editingMultiPartChild.childQty : null}
        />
      )}
      <DialogTitle>{Translate.get("EditPart")}</DialogTitle>
      <DialogContent>
        {!isLoading && (
          <Grid container spacing={5} paddingTop={1}>
            <Grid container spacing={1.5} size={12}>
              <Grid size={12}>
                <InnerTextField
                  required
                  labelKey="Name"
                  inputProps={{ maxLength: 255 }}
                  value={part.description}
                  handleUpdateValue={(newValue) => handleUpdatePartValue("description", newValue)}
                />
              </Grid>
              <Grid size={6}>
                <InnerTextField
                  required
                  labelKey="PartNoLabel"
                  inputProps={{ maxLength: 20 }}
                  value={part.partNo}
                  handleUpdateValue={(newValue) => handleUpdatePartValue("partNo", newValue)}
                />
              </Grid>
              <Grid container spacing={0.5} size={6}>
                <Grid size={9}>
                  <InnerTextField
                    labelKey="Price"
                    value={
                      selectedCurrencyCode
                        ? partPrices.find((pp) => pp.currency === selectedCurrencyCode)?.price
                        : undefined
                    }
                    handleUpdateValue={(newValue) => handleUpdatePartPriceValue(selectedCurrencyCode, newValue)}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      type: "number",
                      min: 0,
                    }}
                  />
                </Grid>
                <Grid size={3}>
                  <InnerSelectFromList
                    labelKey="CurrencyLabel"
                    list={currencies}
                    itemLabelName="code"
                    itemValueName="code"
                    value={selectedCurrencyCode}
                    handleUpdateValue={(newValue) => setSelectedCurrencyCode(newValue)}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={1.5} size={12}>
              <Grid size={6}>
                <InnerSelectFromEnumObject
                  required
                  labelKey="PartTypeLabel"
                  enumObject={partType}
                  translateFn={Translate.getPartType}
                  value={part.partType}
                  disabledFn={(pt) => _disabledPartTypes.includes(pt)}
                  hideFn={(pt) => _disabledPartTypes.includes(pt) && pt !== part.partType}
                  handleUpdateValue={(newValue) => handleUpdatePartValue("partType", newValue)}
                />
              </Grid>
              <Grid size={6}>
                <InnerSelectFromList
                  labelKey="PickAreaLabel"
                  list={areas}
                  itemLabelName="shortName"
                  itemValueName="areaId"
                  value={part.pickAreaId}
                  handleUpdateValue={(newValue) => handleUpdatePartValue("pickAreaId", newValue)}
                />
              </Grid>
              <Grid size={6}>
                <InnerSelectFromEPortCodes
                  labelKey="DepartmentLabel"
                  codes={departmentCodes}
                  value={part.department}
                  handleUpdateValue={(newValue) => handleUpdatePartValue("department", newValue)}
                />
              </Grid>
              <Grid size={6}>
                <InnerSelectFromEPortCodes
                  required
                  labelKey="AccountNoLabel"
                  codes={accountNoCodes}
                  value={part.accountNo}
                  handleUpdateValue={(newValue) => handleUpdatePartValue("accountNo", newValue)}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1.5} size={12}>
              <Grid size={6}>
                <InnerTextField
                  labelKey="OrderPointLabel"
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    type: "number",
                    min: 0,
                  }}
                  value={part.orderPoint}
                  handleUpdateValue={(newValue) => handleUpdatePartValue("orderPoint", newValue)}
                />
              </Grid>
              <Grid size={6}>
                <InnerTextField
                  labelKey="OrderQtyLabel"
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    type: "number",
                    min: 0,
                  }}
                  value={part.orderQty}
                  handleUpdateValue={(newValue) => handleUpdatePartValue("orderQty", newValue)}
                />
              </Grid>
              <Grid size={6}>
                <InnerTextField
                  labelKey="PartRevisionLabel"
                  inputProps={{ maxLength: 50 }}
                  value={part.revision}
                  handleUpdateValue={(newValue) => handleUpdatePartValue("revision", newValue)}
                />
              </Grid>
              <Grid
                alignContent="center"
                onClick={() => handleUpdatePartValue("isDisabled", !part.isDisabled)}
                size={6}
              >
                <Tooltip
                  placement="top-end"
                  title={`${Translate.get("PartIsActive")} : ${
                    part.isDisabled ? Translate.get("No") : Translate.get("Yes")
                  }`}
                >
                  {Translate.get("PartIsActive")} <Switch checked={!part.isDisabled} />
                </Tooltip>
              </Grid>
              {/* <Grid size={6}>
                <DatePicker displayWeekNumber={hasSomeAccess(accessKeys.swedishCompany, accessKeys.isMASystem)}
                  inputFormat={getCurrentInputFormat()}
                  mask={getCurrentDateMask()}
                  label={Translate.get("PartRevDateLabel")}
                  value={part.revDate ?? null}
                  onChange={(newValue) => handleUpdatePartValue("revDate", newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid> */}
            </Grid>

            <Grid container spacing={1} size={12}>
              <Grid size={12}>
                <Accordion
                  className="partDialogAccordion"
                  disableGutters
                  defaultExpanded={part.children && part.children.length > 0}
                >
                  <AccordionSummary expandIcon={<ExpandMore />} aria-controls="content" id="header">
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                      }}
                    >
                      {Translate.get("MultiPartLabel")}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DataGrid
                      showToolbar
                      apiRef={apiRef}
                      className={"dgDynIconText dgSmall"}
                      rows={part.children ?? []}
                      columns={multiPartColumns}
                      disableRowSelectionOnClick
                      getRowId={(row) => row.child.partId}
                      autoHeight={true}
                      slots={{
                        toolbar: () => ToolBar({ showExport: true, apiRef }),
                        noRowsOverlay: CustomNoRowsOverlay(
                          Translate.get("AddChildrenForMultiPart"),
                          Translate.get("NoChildren"),
                        ),
                      }}
                      slotProps={{
                        columnsPanel: { className: "customColumnPanel" },
                        filterPanel: { className: "customfilterPanel" },
                        panel: { className: "customPanel" },
                      }}
                      localeText={localizedTextsMap}
                      disableColumnMenu
                      disableDensitySelector={true}
                      hideFooter={true}
                    />
                    <Button
                      onClick={() => setShowMultiPartChildDialog(true)}
                      startIcon={<Add />}
                      disabled={!part.partId}
                    >
                      {Translate.get("AddChild")}
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => (onClose ? onClose() : window.history.back())}>{Translate.get("Cancel")}</Button>
        <ButtonWithSpinner
          onClick={async () => await handleSaveDialog(part, partPrices)}
          disabled={!part.description || !part.partNo || !part.partType || !part.accountNo}
        >
          {Translate.get("Save")}
        </ButtonWithSpinner>
      </DialogActions>
      {errorMessage && (
        <AlertDialog
          bodyText={errorMessage}
          buttonText={Translate.get("Ok")}
          open
          onClose={() => setErrorMessage(null)}
        />
      )}
    </>
  );
}

function InnerTextField({ labelKey, inputProps, value, handleUpdateValue, required }) {
  return (
    <TextField
      required={required}
      fullWidth
      value={value ?? ""}
      InputProps={{ spellCheck: false }}
      onChange={(event) => handleUpdateValue(event.target.value)}
      label={Translate.get(labelKey)}
      inputProps={inputProps}
    />
  );
}

function InnerSelect({ labelKey, items, value, handleUpdateValue, disabledFn, hideFn, required }) {
  let sortedItems = items ? [...items].sort((a, b) => a.label.localeCompare(b.label)) : items;
  return (
    <FormControl fullWidth required={required}>
      <InputLabel id={labelKey + "id"}>{Translate.get(labelKey)}</InputLabel>
      <Select
        labelId={labelKey + "id"}
        id={labelKey + "select"}
        value={value ?? ""}
        label={Translate.get(labelKey)}
        onChange={(event) => handleUpdateValue(event.target.value)}
      >
        {sortedItems &&
          sortedItems
            .filter((item) => (hideFn ? !hideFn(item.value) : true))
            .map((item, index) => (
              <MenuItem key={index} value={item.value} disabled={disabledFn ? disabledFn(item.value) : false}>
                {item.label}
              </MenuItem>
            ))}
      </Select>
    </FormControl>
  );
}

function InnerSelectFromEnumObject({
  labelKey,
  enumObject,
  translateFn,
  value,
  handleUpdateValue,
  disabledFn,
  hideFn,
  required,
}) {
  const items = Object.values(enumObject).map((v) => ({ label: translateFn(v), value: v }));
  return (
    <InnerSelect
      labelKey={labelKey}
      items={items}
      value={value}
      handleUpdateValue={handleUpdateValue}
      disabledFn={disabledFn}
      hideFn={hideFn}
      required={required}
    />
  );
}

function InnerSelectFromEPortCodes({ labelKey, codes, value, handleUpdateValue, required }) {
  const items = codes.map((code) => ({ label: code.description, value: code.ePortCodeId }));
  return (
    <InnerSelect
      labelKey={labelKey}
      items={items}
      value={value}
      handleUpdateValue={handleUpdateValue}
      required={required}
    />
  );
}

function InnerSelectFromList({ labelKey, list, itemLabelName, itemValueName, value, handleUpdateValue, compareFn }) {
  const items = list.map((item) => ({ label: item[itemLabelName], value: item[itemValueName] }));
  return (
    <InnerSelect
      labelKey={labelKey}
      items={items}
      value={value}
      handleUpdateValue={handleUpdateValue}
      compareFn={compareFn}
    />
  );
}

async function loadEPortCodes(codeType, api) {
  const url = `${import.meta.env.VITE_MAIN_URL}codes/${codeType}`;
  const codesResponse = await api.fetchWithOverride(
    url,
    false,
    "GET",
    (response) => response && Array.isArray(response),
  );
  if (codesResponse && Array.isArray(codesResponse)) {
    const defaultCode = codesResponse.filter((c) => c.isDefaultValue);
    return {
      ePortCodes: codesResponse,
      defaultEPortCodeId: defaultCode.length === 1 ? defaultCode[0].ePortCodeId : null,
    };
  } else {
    return null;
  }
}

async function loadAreas(api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/areas`;
  const response = await api.fetch(url, false, "GET");
  if (response.isSuccessful) {
    return response.value;
  } else {
    return null;
  }
}

async function loadPartPrices(partId, api) {
  if (!partId) return null;

  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/parts/${partId}/prices`;
  const response = await api.fetch(url, false, "GET");
  if (response.isSuccessful) {
    return response.value;
  } else {
    return null;
  }
}

async function savePartPrices(partId, partPrices, api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/parts/${partId}/prices`;
  const response = await api.fetch(url, partPrices, "POST");
  return response;
}

async function loadCurrencies(api) {
  const url = `${import.meta.env.VITE_MAIN_URL}orders/currencies`;
  const response = await api.fetchWithOverride(url, false, "GET", (response) => response && Array.isArray(response));
  if (response && Array.isArray(response)) {
    const defaultCurrency = response.filter((c) => c.isDefault);
    return {
      currencies: response,
      defaultCurrencyCode: defaultCurrency.length === 1 ? defaultCurrency[0].code : null,
    };
  } else {
    return null;
  }
}

async function loadPart(partId, api) {
  if (!partId) return null;

  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/parts/${partId}`;
  const response = await api.fetch(url, false, "GET");
  return response.isSuccessful ? response : null;
}

async function savePart(part, api) {
  const url = `${import.meta.env.VITE_MAIN_URL}warehouse/parts`;
  const response = await api.fetch(url, part, "POST");
  return response;
}
